import AnimatedHero from '../components/AnimatedHero';
import Ankh from '../components/Ankh';
import InvitationBlock from '../components/marketing/InvitationBlock';
import React from 'react';

// ═══════════════════════════════════════════════════════════════════════════
//  CLEO'S SECRET — LANDING PAGE
//  Reconciled to docs/home-page-copy.md (locked May 2026). 8-section arc:
//    1. Hero (AnimatedHero) — premise + title + subtitle + CTA + credibility
//    2. Promise — what you leave with
//    2.5. Single line of proof — italic centred patient quote
//    3. The Belief — Cleopatra invoked ONCE here + cost-of-waiting close
//    4. Journey preview — 4 verb-named steps (Prepare/Investigate/Consult/Carry forward)
//    5. Who it's for — 4 vignettes in 2x2 grid
//    7. Stories teaser — placeholder pull-quote + Read more stories CTA
//    8. Invitation — InvitationBlock atom on dark
//  Removed per doc: "Why we exist" 3-pillar colonnade (lives on Approach),
//  Membership tiers (lives on Membership), final-CTA audit prompt (replaced
//  by InvitationBlock), founding-principle pull-quote (retired).
//  Em dashes converted: §2 Promise body, §3 Belief body parenthetical,
//  §4 Journey step 3, §5 Who-it's-for subtitle.
// ═══════════════════════════════════════════════════════════════════════════

// ───── FAMILIES ─────
const FF_SERIF  = "'Cormorant Garamond', Georgia, serif";
const FF_UI     = "system-ui, -apple-system, sans-serif";
const FF_BRAND  = "'Cinzel', 'Cormorant Garamond', Georgia, serif"; // wordmark + small-caps attributions ONLY

// ───── PALETTE ─────
const PAP   = '#FAF6EE';   // Papyrus
const SAND  = '#E8DDC4';   // Warm cream section bg
const SAND3 = '#C8B888';   // Cream border accent
const OBS   = '#1D1116';   // Plum noir
const GO    = '#C9A961';   // Primary gold
const GO2   = '#E0C685';   // Light gold (italic emphasis on dark)
const BR    = '#9A7430';   // Bronze accent
const BR2   = '#5A3F18';   // Mahogany bronze — italic emphasis on cream
const INK   = '#1A0F08';
const TX    = '#2C1810';
const TF    = '#7A6040';
const IVY   = '#F0E8D4';

// ───── TYPE SYSTEM ─────
const T = {
  display:    { fontFamily: FF_SERIF, fontSize: 'clamp(40px, 6.4vw, 92px)', fontWeight: 400, lineHeight: 1.08, letterSpacing: '-0.022em', margin: 0 },
  quote:      { fontFamily: FF_SERIF, fontSize: 'clamp(24px, 2.9vw, 38px)', fontWeight: 400, fontStyle: 'italic', lineHeight: 1.4, letterSpacing: '-0.008em', margin: 0 },
  label:      { fontFamily: FF_SERIF, fontSize: 'clamp(13px, 1.1vw, 16px)', fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase', margin: 0 },
  brandMark:  { fontFamily: FF_BRAND, fontSize: 'clamp(18px, 1.6vw, 24px)', fontWeight: 500, letterSpacing: '0.26em', textTransform: 'uppercase', margin: 0 },
  eyebrow:    { fontFamily: FF_SERIF, fontSize: 'clamp(18px, 1.6vw, 22px)', fontWeight: 700, letterSpacing: '0.34em', textTransform: 'uppercase' },
  bodyLg:     { fontFamily: FF_SERIF, fontSize: 'clamp(18px, 1.9vw, 23px)', fontWeight: 400, lineHeight: 1.7, margin: 0 },
  bodyMd:     { fontFamily: FF_SERIF, fontSize: 'clamp(16px, 1.5vw, 19px)', fontWeight: 400, lineHeight: 1.65, margin: 0 },
  bodySm:     { fontFamily: FF_SERIF, fontSize: 'clamp(14px, 1.2vw, 16px)', fontWeight: 400, lineHeight: 1.6, margin: 0 },
  numeralSm:  { fontFamily: FF_SERIF, fontSize: 'clamp(32px, 4vw, 58px)', fontWeight: 300, lineHeight: 1, letterSpacing: '-0.025em' },
};

// ───── LAYOUT TOKENS ─────
const SECTION_PADDING = 'clamp(72px, 8.5vw, 128px) clamp(24px, 4.5vw, 64px)';

// ═══ ATOMS (page-local) ═══

const Eyebrow = ({ children, center = false, color = BR2 }) => (
  <div style={{
    ...T.eyebrow, color,
    display: 'flex', alignItems: 'center', gap: 18,
    justifyContent: center ? 'center' : 'flex-start',
  }}>
    <span style={{ width: 1.5, height: 18, background: color, opacity: 0.85 }} aria-hidden="true" />
    <span>{children}</span>
    {center && <span style={{ width: 1.5, height: 18, background: color, opacity: 0.85 }} aria-hidden="true" />}
  </div>
);

// SectionDivider — gold rule + diamond + gold rule
const SectionDivider = ({ color = GO, opacity = 0.7 }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 'clamp(18px, 2vw, 28px)',
    marginBottom: 'clamp(48px, 6vw, 80px)',
  }} aria-hidden="true">
    <div style={{ width: 'clamp(120px, 18vw, 240px)', height: 1, background: color, opacity }} />
    <div style={{
      width: 14, height: 14, background: color,
      transform: 'rotate(45deg)', opacity: Math.min(opacity + 0.2, 0.95),
    }} />
    <div style={{ width: 'clamp(120px, 18vw, 240px)', height: 1, background: color, opacity }} />
  </div>
);

// Text-link CTA — italic Cormorant with hover-underline. Editorial register,
// quieter than BtnPrimary. Used by §4 "See the full pathway →" and §7 "Read
// more stories →" CTAs.
const TextLinkCta = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      fontFamily: FF_SERIF,
      fontSize: 'clamp(16px, 1.5vw, 19px)',
      fontStyle: 'italic',
      fontWeight: 500,
      color: BR2,
      background: 'transparent',
      border: 'none',
      borderBottom: '1px solid rgba(90, 63, 24, 0.35)',
      padding: '4px 0',
      cursor: 'pointer',
      transition: 'border-color 0.2s, color 0.2s',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.borderColor = BR2;
      e.currentTarget.style.color = INK;
    }}
    onMouseLeave={e => {
      e.currentTarget.style.borderColor = 'rgba(90, 63, 24, 0.35)';
      e.currentTarget.style.color = BR2;
    }}
  >{children}</button>
);

// ═══ RESPONSIVE STYLES ═══
const RESPONSIVE_CSS = `
  .cs-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); position: relative; }
  .cs-journey-spine { position: absolute; top: 65px; left: 6%; right: 6%; height: 1.5px; background: ${GO}; opacity: 0.8; }
  @media (max-width: 880px) {
    .cs-grid-4 { grid-template-columns: 1fr 1fr; row-gap: 56px; }
    .cs-journey-spine { display: none; }
  }
  @media (max-width: 520px) {
    .cs-grid-4 { grid-template-columns: 1fr; }
  }
  .cs-vignette-grid { display: grid; grid-template-columns: 1fr; gap: clamp(28px, 3vw, 48px); }
  @media (min-width: 720px) {
    .cs-vignette-grid { grid-template-columns: 1fr 1fr; }
  }
  .cs-footer-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 48px; }
  @media (max-width: 720px) {
    .cs-footer-grid { grid-template-columns: 1fr; gap: 36px; }
  }
`;

// ═══════════════════════════════════════════════════════════════════════════

export default function LandingPage({ onNavigate }) {

  // §4 Journey — 4 verb-named steps per doc, no specific times surfaced.
  // Em dash from doc Step 3 "begins where most consultations end — your story
  // already in the room" converted to period (platform guardrail).
  const journey_steps = [
    { n: '01', t: 'Prepare',
      b: "Begin with a guided intake. Considered questions, in your own time, that build the full picture of your health, your history, and what you're really here to ask." },
    { n: '02', t: 'Investigate',
      b: 'The right pathology, ordered for your story after your initial consultation. Read closely, not skimmed.' },
    { n: '03', t: 'Consult',
      b: 'The second consultation that begins where most consultations end. Your story already in the room, the data already on the page.' },
    { n: '04', t: 'Carry forward',
      b: 'A plan written for you, and, if you choose, a clinical summary you can share with your GP. So the people caring for you are reading from the same page.' },
  ];

  // §5 Who it's for — 4 vignettes locked per doc.
  // Subtitle em dash converted: "midlife — and for" → "midlife, and for".
  const vignettes = [
    {
      lead: "For the woman whose perimenopause hasn't been taken seriously.",
      body: "She's been told it's stress, or sleep, or just where she is in life. She wants a thorough look at what's actually happening in her body.",
    },
    {
      lead: 'For the woman deep in menopause, wanting a clearer plan.',
      body: 'Symptoms have come and gone and come back. She wants someone to look at the whole picture, not one piece of it at a time.',
    },
    {
      lead: "For the woman whose hormonal story is complicated.",
      body: "Multiple symptoms, multiple specialists, no one yet who's pulled it into a single coherent picture.",
    },
    {
      lead: 'For the woman who simply wants to begin properly.',
      body: "Not in crisis. Just wanting to understand her body well, while there's still time to prepare for what's next.",
    },
  ];

  return (
    <div style={{ background: PAP }}>
      <style>{RESPONSIVE_CSS}</style>

      {/* ═════════ SECTION 1 — HOOK (AnimatedHero, OBS dark) ═════════ */}
      <AnimatedHero onNavigate={onNavigate} />

      {/* ═════════ SECTION 2 — PROMISE (cream / SAND) ═════════ */}
      <section style={{ background: SAND, padding: SECTION_PADDING }}>
        <div style={{ textAlign: 'center', maxWidth: 1280, margin: '0 auto' }}>
          <SectionDivider />
          <Eyebrow center>WHAT YOU LEAVE WITH</Eyebrow>
          <h2 style={{ ...T.display, color: INK, maxWidth: 1280, margin: '32px auto 36px' }}>
            <span style={{ display: 'block' }}>A clearer picture</span>
            <span style={{ display: 'block', fontStyle: 'italic', fontSize: '0.9em', color: BR2, fontWeight: 400 }}>And a plan that&apos;s yours</span>
          </h2>
          <div style={{ width: 'clamp(220px, 28vw, 360px)', height: 3, background: GO, opacity: 1, margin: '0 auto 48px' }} aria-hidden="true" />
          <p style={{ ...T.bodyLg, fontStyle: 'italic', color: BR2, maxWidth: 760, margin: '0 auto 40px' }}>
            The full story heard. The right pathology read closely. A plan written for your body, in language you can act on.
          </p>
          {/* Body — em dash from doc "share with your GP — so the people caring"
              converted to comma per platform guardrail. */}
          <p style={{ ...T.bodyLg, color: TX, maxWidth: 880, margin: '0 auto', textAlign: 'left' }}>
            By the time you sit down with your practitioner, the work of understanding you is already well underway. Your history has been considered. Your patterns have been recognised. Your pathology has been ordered and read in the second consultation. What you leave with is the thing most women in midlife rarely receive: a plan written for your body, on the page, in language you can act on. And, if you&rsquo;d like, a clinical summary to share with your GP, so the people caring for you are reading from the same page.
          </p>
        </div>
      </section>

      {/* ═════════ SECTION 2.5 — SINGLE LINE OF PROOF (continuous SAND) ═════════
          PLACEHOLDER content per docs/home-page-copy.md §2.5. The line below
          is illustrative — names the length of time she waited + specific
          outcome + clinical encounter. TODO: swap for Harvey-provided real
          consented patient sentence (8-15 words, real woman, minimal
          attribution like "A patient, after her diagnostic" or "A patient,
          three months in"). Doc §2.5 is the single highest-leverage
          commercial change to the home page — earns the rest of the scroll. */}
      <section style={{ background: SAND, padding: 'clamp(56px, 8vw, 128px) clamp(24px, 4.5vw, 64px)' }}>
        <div style={{ textAlign: 'center', maxWidth: 760, margin: '0 auto' }}>
          <p style={{
            fontFamily: FF_SERIF,
            fontSize: 'clamp(26px, 3.4vw, 32px)',
            fontStyle: 'italic',
            fontWeight: 400,
            color: BR2,
            lineHeight: 1.4,
            letterSpacing: '-0.005em',
            margin: 0,
          }}>
            &ldquo;I&rsquo;d spent three years being told nothing was wrong. In ninety minutes, someone finally looked at the whole picture.&rdquo;
          </p>
          <div style={{
            fontFamily: FF_BRAND,
            fontSize: 'clamp(11px, 1vw, 13px)',
            fontWeight: 500,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: 'rgba(90, 63, 24, 0.55)',
            marginTop: 32,
          }}>
            A patient, after her diagnostic
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 3 — THE BELIEF (OBS dark) ═════════
          The ONE Cleopatra mention on the home page (subtitle). The brand
          name "Cleo's Secret" carries the frequency everywhere else; the
          page never speaks her name again. Body em dashes from doc
          "perimenopause and menopause — about sleep, mood, bones... — deserve"
          converted to parenthetical. */}
      <section style={{ background: OBS, padding: SECTION_PADDING }}>
        <div style={{ textAlign: 'center', maxWidth: 1280, margin: '0 auto' }}>
          <SectionDivider color={GO2} opacity={0.55} />
          <Eyebrow center color={GO2}>THE BELIEF</Eyebrow>
          <h2 style={{ ...T.display, color: IVY, maxWidth: 1280, margin: '32px auto 36px' }}>
            <span style={{ display: 'block' }}>Midlife questions</span>
            <span style={{ display: 'block', fontStyle: 'italic', fontSize: '0.9em', color: GO2, fontWeight: 400 }}>deserve time</span>
          </h2>
          <div style={{ width: 'clamp(220px, 28vw, 360px)', height: 3, background: GO2, opacity: 0.9, margin: '0 auto 48px' }} aria-hidden="true" />
          <p style={{ ...T.bodyLg, fontStyle: 'italic', color: GO2, maxWidth: 820, margin: '0 auto 40px' }}>
            Cleopatra&rsquo;s secret wasn&rsquo;t beauty, it was being properly prepared long before any decision was made.
          </p>
          <p style={{ ...T.bodyLg, color: 'rgba(240, 232, 212, 0.86)', maxWidth: 820, margin: '0 auto', textAlign: 'left' }}>
            Cleo&rsquo;s Secret was built on the same idea, translated into modern clinical practice. The questions that arrive in perimenopause and menopause (about sleep, mood, bones, cycles, energy, identity) deserve to be prepared for, not squeezed in. Here, the preparation happens first. Your full intake. The right pathology. Your patterns, considered carefully. By the time your consultation begins, your story is already in the room. So is the data.
          </p>

          {/* Cost-of-waiting close — 2-liner italic gold centred, generous
              spacing above and below per doc spec. */}
          <div style={{ marginTop: 'clamp(64px, 7vw, 112px)' }}>
            <p style={{ ...T.quote, color: GO2, marginBottom: 14 }}>
              The longer the right answers wait, the more is at stake.
            </p>
            <p style={{ ...T.quote, color: GO2 }}>
              Midlife is not a season to coast through.
            </p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 4 — JOURNEY PREVIEW (cream / SAND) ═════════ */}
      <section style={{ background: SAND, padding: SECTION_PADDING }}>
        <div style={{ textAlign: 'center', maxWidth: 1280, margin: '0 auto 72px' }}>
          <SectionDivider />
          <Eyebrow center>THE JOURNEY</Eyebrow>
          <h2 style={{ ...T.display, color: INK, margin: '32px auto 36px' }}>
            <span style={{ display: 'block' }}>A path,</span>
            <span style={{ display: 'block', fontStyle: 'italic', fontSize: '0.9em', color: BR2, fontWeight: 400 }}>properly mapped</span>
          </h2>
          <div style={{ width: 'clamp(220px, 28vw, 360px)', height: 3, background: GO, opacity: 1, margin: '0 auto 48px' }} aria-hidden="true" />
          <p style={{ ...T.bodyLg, color: TX, maxWidth: 720, margin: '0 auto' }}>
            Four considered steps, from first questions to a plan you can carry forward.
          </p>
        </div>

        {/* Procession — 4 stations. Specific times dropped per doc (live
            on Approach instead). */}
        <div style={{ maxWidth: 1680, margin: '0 auto' }}>
          <div className="cs-grid-4">
            <div className="cs-journey-spine" aria-hidden="true" />
            {journey_steps.map((s) => (
              <div key={s.n} style={{ padding: '0 clamp(12px, 1.6vw, 24px)' }}>
                {/* Above-spine spacer — keeps diamond aligned with the gold
                    spine at top: 65px. Doc-locked dropping of cat/time stack. */}
                <div style={{ height: 50 }} aria-hidden="true" />

                {/* Station diamond — sits on the spine */}
                <div style={{
                  width: 18, height: 18, background: GO,
                  transform: 'rotate(45deg)',
                  margin: '6px auto',
                  border: `2px solid ${SAND}`,
                  boxSizing: 'content-box',
                }} aria-hidden="true" />

                {/* Below-spine: numeral + hairline + verb-name + body */}
                <div style={{ paddingTop: 30, textAlign: 'center' }}>
                  <div style={{ ...T.numeralSm, color: GO, opacity: 0.7 }}>{s.n}</div>
                  <div style={{ width: 'clamp(36px, 3.4vw, 48px)', height: 1, background: GO, opacity: 0.55, margin: '20px auto 24px' }} aria-hidden="true" />
                  <div style={{
                    fontFamily: FF_SERIF,
                    fontSize: 'clamp(22px, 2.4vw, 28px)',
                    fontStyle: 'italic',
                    fontWeight: 500,
                    color: BR2,
                    marginBottom: 18,
                    letterSpacing: '-0.005em',
                  }}>{s.t}</div>
                  <p style={{ ...T.bodyMd, color: TX, maxWidth: 320, margin: '0 auto' }}>{s.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* See the full pathway → text-link CTA → ApproachPage (URL id 'about') */}
        <div style={{ textAlign: 'center', marginTop: 'clamp(56px, 6vw, 88px)' }}>
          <TextLinkCta onClick={() => onNavigate('about')}>
            See the full pathway &rarr;
          </TextLinkCta>
        </div>
      </section>

      {/* ═════════ SECTION 5 — WHO IT'S FOR (papyrus / PAP inset) ═════════
          PAP between SAND surfaces reads as a quiet papyrus inset — the
          editorial register where the woman reading recognises herself. */}
      <section style={{ background: PAP, padding: SECTION_PADDING }}>
        <div style={{ textAlign: 'center', maxWidth: 1280, margin: '0 auto 64px' }}>
          <SectionDivider />
          <Eyebrow center>FOR THE WOMAN WHO</Eyebrow>
          <h2 style={{ ...T.display, color: INK, margin: '32px auto 36px', maxWidth: 1280 }}>
            <span style={{ display: 'block' }}>For the women who are ready</span>
            <span style={{ display: 'block', fontStyle: 'italic', fontSize: '0.9em', color: BR2, fontWeight: 400 }}>to be properly heard</span>
          </h2>
          <div style={{ width: 'clamp(220px, 28vw, 360px)', height: 3, background: GO, opacity: 1, margin: '0 auto 48px' }} aria-hidden="true" />
          {/* Subtitle em dash converted: "midlife — and for" → "midlife, and for" */}
          <p style={{ ...T.bodyLg, fontStyle: 'italic', color: BR2, maxWidth: 760, margin: '0 auto' }}>
            Cleo&rsquo;s Secret was built for the questions of midlife, and for the women asking them carefully.
          </p>
        </div>

        {/* 4 vignettes in 2x2 grid (cs-vignette-grid: 1-col <720px, 2-col ≥720px).
            Each = italic gold lead line + 1 plain body sentence. */}
        <div className="cs-vignette-grid" style={{ maxWidth: 1080, margin: '0 auto' }}>
          {vignettes.map((v, i) => (
            <div key={i} style={{
              padding: 'clamp(28px, 3vw, 40px) clamp(20px, 2.5vw, 32px)',
              textAlign: 'left',
            }}>
              <p style={{
                ...T.bodyLg,
                fontStyle: 'italic',
                color: BR2,
                marginBottom: 14,
              }}>{v.lead}</p>
              <p style={{
                ...T.bodyMd,
                color: TX,
              }}>{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═════════ SECTION 7 — STORIES TEASER (cream / SAND) ═════════
          PLACEHOLDER pull-quote — reused from docs/stories-page-copy.md
          Card 1 (EMMA). TODO: when Harvey selects 1-2 real consented patient
          stories per doc spec, swap this placeholder for the chosen quote(s). */}
      <section style={{ background: SAND, padding: SECTION_PADDING }}>
        <div style={{ textAlign: 'center', maxWidth: 1280, margin: '0 auto' }}>
          <SectionDivider />
          <Eyebrow center>WHAT WOMEN HAVE FOUND HERE</Eyebrow>
          <h2 style={{ ...T.display, color: INK, margin: '32px auto 36px', maxWidth: 1280 }}>
            <span style={{ display: 'block' }}>Quiet outcomes,</span>
            <span style={{ display: 'block', fontStyle: 'italic', fontSize: '0.9em', color: BR2, fontWeight: 400 }}>properly arrived at</span>
          </h2>
          <div style={{ width: 'clamp(220px, 28vw, 360px)', height: 3, background: GO, opacity: 1, margin: '0 auto 48px' }} aria-hidden="true" />
          <p style={{ ...T.bodyLg, fontStyle: 'italic', color: BR2, maxWidth: 760, margin: '0 auto clamp(56px, 6vw, 88px)' }}>
            Real women, real preparation, real shifts in how they feel in their own bodies.
          </p>

          {/* Pull-quote + attribution — PLACEHOLDER per code-comment above */}
          <div style={{ maxWidth: 780, margin: '0 auto' }}>
            <p style={{
              fontFamily: FF_SERIF,
              fontSize: 'clamp(22px, 2.6vw, 28px)',
              fontStyle: 'italic',
              fontWeight: 400,
              color: BR2,
              lineHeight: 1.45,
              margin: '0 0 24px',
            }}>
              &ldquo;I&rsquo;d been told three different things by three different doctors. Cleo&rsquo;s Secret was the first place that looked at all of it at once.&rdquo;
            </p>
            <div style={{
              fontFamily: FF_BRAND,
              fontSize: 'clamp(11px, 1vw, 13px)',
              fontWeight: 500,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: 'rgba(90, 63, 24, 0.55)',
            }}>
              Emma &middot; 47 &middot; Three months in
            </div>
          </div>

          {/* Read more stories → text-link CTA */}
          <div style={{ marginTop: 'clamp(48px, 5vw, 72px)' }}>
            <TextLinkCta onClick={() => onNavigate('stories')}>
              Read more stories &rarr;
            </TextLinkCta>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 8 — INVITATION (OBS dark) ═════════ */}
      <section style={{ background: OBS, padding: SECTION_PADDING, color: IVY }}>
        <InvitationBlock onDark onCtaClick={() => onNavigate('intake')} />
      </section>

      {/* ═══ CLINICAL DISCLOSURE (legal compliance — unchanged) ═══ */}
      <div style={{ background: OBS, padding: 'clamp(36px, 4vw, 52px) clamp(20px, 4vw, 48px)', borderTop: '0.5px solid rgba(201,169,97,0.18)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', display: 'flex', gap: 32, alignItems: 'stretch' }}>
          <div style={{ flexShrink: 0, width: 3, background: GO, opacity: 0.6, alignSelf: 'stretch' }} aria-hidden="true" />
          <div>
            <div style={{ ...T.label, color: 'rgba(224,198,133,0.88)', marginBottom: 16 }}>Important clinical information</div>
            <p style={{ ...T.bodySm, color: 'rgba(240,232,212,0.75)' }}>
              Cleo&apos;s Secret provides GP-led clinical assessment and support. Our programs support and do not replace your relationship with your regular treating practitioner. All health decisions are made by you in consultation with our clinical team. Information provided does not constitute medical advice. AHPRA registered practitioners only. For emergencies call 000.
            </p>
          </div>
        </div>
      </div>

      {/* ═══ FOOTER (unchanged this session — backlog: "sanctuary" + Melbourne
          tagline violate platform vocabulary guardrails but doc allows in
          footer context; global /partners link deferred until /partners route
          exists; nav links pre-date the 5-top-level flatten; treat as a
          separate footer-hygiene pass) ═══ */}
      <footer style={{ background: OBS, padding: 'clamp(56px, 7vw, 80px) clamp(20px, 4vw, 48px) 48px', borderTop: '0.5px solid rgba(201,169,97,0.22)' }}>
        <div className="cs-footer-grid" style={{ maxWidth: 1680, margin: '0 auto' }}>
          <div>
            <div style={{ marginBottom: 22 }} aria-hidden="true">
              <Ankh responsive="footer" color={GO2} />
            </div>
            <div style={{ ...T.brandMark, color: GO2, marginBottom: 14 }}>Cleo&apos;s Secret</div>
            <div style={{
              fontFamily: FF_SERIF, fontSize: 'clamp(14px, 1.2vw, 17px)', fontWeight: 500, fontStyle: 'italic',
              color: 'rgba(224,198,133,0.82)', letterSpacing: '0.04em', marginBottom: 28, lineHeight: 1.7,
            }}>
              Women&apos;s Health Sanctuary<br />
              Melbourne, Victoria
            </div>
            <div style={{ ...T.bodySm, color: 'rgba(224,198,133,0.55)' }}>
              &copy; 2026 Cleo&apos;s Secret Pty Ltd &middot; ABN 00 000 000 000<br />
              AHPRA compliant &middot; GP-led &middot; For emergencies call 000
            </div>
          </div>
          <div>
            <div style={{ ...T.eyebrow, color: 'rgba(224,198,133,0.75)', marginBottom: 24 }}>Navigate</div>
            {['How it works', 'Programs and assessments', 'Membership', 'About Cleo’s Secret'].map(l => (
              <div key={l} style={{ ...T.bodyMd, color: 'rgba(240,232,212,0.78)', marginBottom: 14, cursor: 'pointer' }}>{l}</div>
            ))}
          </div>
          <div>
            <div style={{ ...T.eyebrow, color: 'rgba(224,198,133,0.75)', marginBottom: 24 }}>Legal and compliance</div>
            {['Privacy policy', 'Terms of service', 'AHPRA compliance', 'hello@cleossecret.com.au'].map(l => (
              <div key={l} style={{ ...T.bodyMd, color: 'rgba(240,232,212,0.78)', marginBottom: 14, cursor: 'pointer' }}>{l}</div>
            ))}
          </div>
        </div>
      </footer>

    </div>
  );
}
